/**
 * Shows Q&A for a completed assessment run. Uses run.answers (itemId -> value) and assessmentSpec.items to show labels.
 * Displays superText (above prompt), label (prompt), subText (helper text below prompt), and helpText as tooltip when present.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material'

function getAnswerLabel(item, value) {
  if (value == null || value === '') return '—'
  if (Array.isArray(value)) {
    const opts = item?.options ?? []
    const labels = value.map((id) => {
      const o = opts.find((opt) => (opt.optionId ?? opt.id) === id)
      return o?.label ?? o?.title ?? id
    })
    return labels.join(', ')
  }
  const opts = item?.options ?? []
  const opt = opts.find((o) => (o.optionId ?? o.id) === value)
  return opt?.label ?? opt?.title ?? String(value)
}

export default function PractitionerAssessmentAnswersView({ run, assessmentSpec, clinicalAssessment, onBack }) {
  const answers = run?.run?.answers ?? run?.answers ?? {}
  const items = assessmentSpec?.items ?? []
  const journalQusAns = assessmentSpec?.journalQusAns ?? clinicalAssessment?.journalQusAns ?? []
  const itemsById = new Map((items || []).map((i) => [i.itemId, i]))
  const journalByQusId = new Map((journalQusAns || []).map((j) => [(j.qusId ?? j.questionId), j]))

  const entries = Object.entries(answers).filter(([, v]) => v != null && v !== '' && (Array.isArray(v) ? v.length > 0 : true))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <IconButton size="small" onClick={onBack} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          Answers — {run?.assessmentTitle ?? 'Assessment'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {entries.length === 0 ? (
          <Typography color="text.secondary">No answers recorded for this run.</Typography>
        ) : (
          entries.map(([itemId, value], idx) => {
            const item = itemsById.get(itemId)
            const journalItem = journalByQusId.get(itemId)
            const label = item?.label ?? journalItem?.question ?? `Question ${idx + 1}`
            const answerLabel = getAnswerLabel(item, value)
            const superText =
              item?.superText ?? item?.supertext ?? journalItem?.superText ?? journalItem?.supertext ?? null
            const subText =
              item?.subText ?? item?.subtext ?? journalItem?.subText ?? journalItem?.subtext ?? null
            const helpText =
              item?.helpText ?? item?.helptext ?? journalItem?.helpText ?? journalItem?.helptext ?? null
            return (
              <Paper key={itemId} elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                {superText && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textTransform: 'uppercase',
                      letterSpacing: 1.2,
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: '0.75rem',
                    }}
                  >
                    {String(superText).toUpperCase()}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} component="span" sx={{ display: 'block', color: 'text.primary' }}>
                      {label}
                    </Typography>
                    {subText && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontSize: '0.8125rem',
                          lineHeight: 1.5,
                          color: '#64748b',
                          bgcolor: 'action.hover',
                          px: 1,
                          py: 0.75,
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderColor: 'info.light',
                        }}
                      >
                        {subText}
                      </Typography>
                    )}
                  </Box>
                  {helpText && (
                    <Tooltip title={helpText} arrow placement="top">
                      <IconButton size="small" sx={{ p: 0.25, mt: -0.5 }} aria-label="Help">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {answerLabel}
                </Typography>
              </Paper>
            )
          })
        )}
      </Box>
    </Box>
  )
}
